/**
 * Signing through YouSign, in the draft header: the letter's ONE status (`letterStatus`, the same
 * badge as on the lists), the line under the name that says what it means, and the actions the
 * stage allows — send (asked to confirm first, because YouSign emails the supervisor at once),
 * cancel while it is out (also confirmed), the signed PDF, and "File in Personio" after a filing
 * that certainly failed. One main button per stage (David, Oct 15 2026).
 */
import { useEffect, useRef, useState } from 'react';
import { Banner } from '@finn-auto/design-system/atoms/banner';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { FileCheck2, FolderUp, Loader2, Send } from 'lucide-react';
import { ModalActions } from '../taActionBoard/ModalActions';
import { formatDate, markedDoneText } from './format';
import { SENDABLE } from './signatureLabels';
import { letterStatus } from './letterStatus';
import { LetterStatusBadge } from './LetterStatusBadge';
import { sendIsMain } from './headerButtons';
import type { LetterSignature } from './useLetterSignature';
import type { DraftRequestBody, ReferenceLetterDraft } from '../../types/referenceLetterDraft';

/** An ISO time as the page writes a day ("14 Oct 2026"); null stays null. */
const day = (iso: string | null | undefined) => (iso ? formatDate(iso.slice(0, 10)) : null);


/** Why Send is not offered right now, or null when it is. Shown as the button's description. */
function sendBlocker(draft: ReferenceLetterDraft, request: DraftRequestBody | null, updating: boolean): string | null {
  if (updating || !request) return 'The draft is updating.';
  if (!draft.downloadable) return 'The draft still has open points.';
  // `null` is the proxy saying there is nobody; a missing field (an older proxy) is left to it.
  if (draft.signatory === null) return 'Personio has no signing supervisor for this letter.';
  if (draft.signatory && !draft.signatory.active) return `${draft.signatory.name} no longer works at FINN.`;
  return null;
}

/**
 * The confirmation, kept apart from the header so the header renders nothing extra while closed.
 * Deliberately NOT named "Modal…": ModalContent clones direct children whose name starts with
 * "Modal" (see InterviewRescheduleButton).
 */
function ConfirmDialog({ title, body, confirmLabel, onConfirm, onClose }: { title: string; body: string; confirmLabel: string; onConfirm: () => void; onClose: () => void }) {
  return (
    <Modal open onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,30rem)] gap-4 rounded p-6">
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
        </ModalHeader>
        <p className="text-sm text-zinc-700">{body}</p>
        <ModalActions confirmLabel={confirmLabel} onConfirm={onConfirm} onCancel={onClose} />
      </ModalContent>
    </Modal>
  );
}

const SEND_REASON_ID = 'signature-send-reason';
const ERROR_ID = 'signature-error';

/**
 * The letter's status at the head of the button row, as tall as the buttons (David, Oct 15 2026):
 * the lists' own badge, so the header and the lists cannot word it two ways. Focus lands here
 * after a send or cancel.
 */
export function HeaderStatus({ sig }: { sig: LetterSignature }) {
  if (!sig.view) return null;
  const status = letterStatus({ tasksSubmitted: true, evaluationSubmitted: true, signature: sig.view.state, personio: sig.view.personio, done: Boolean(sig.view.done) });
  return (
    <span id={STATE_ID} tabIndex={-1} className="rounded outline-none focus-visible:ring-2 focus-visible:ring-trustedBlue">
      <LetterStatusBadge status={status} className="h-10 px-3" />
    </span>
  );
}

const STATE_ID = 'letter-state';

/** The actions the stage allows, for the draft header (Download .docx is the workspace's own). */
export function SignatureControl({ draft, request, updating, sig }: { draft: ReferenceLetterDraft; request: DraftRequestBody | null; updating: boolean; sig: LetterSignature }) {
  const [asking, setAsking] = useState<'send' | 'cancel' | null>(null);
  // After a confirmed send or cancel the button that was clicked is gone or disabled, so focus
  // would fall to the page: it goes to the new state instead, once the proxy has answered.
  const acted = useRef(false);
  useEffect(() => {
    if (!acted.current || sig.busy) return;
    acted.current = false;
    // A refusal's error first — it says why nothing changed — else the new state.
    (document.getElementById(ERROR_ID) ?? document.getElementById(STATE_ID))?.focus();
  }, [sig.busy]);
  const state = sig.view?.state;
  const signer = sig.view?.signerName || draft.signatory?.name || 'the supervisor';
  const blocker = sendBlocker(draft, request, updating);

  const refileable = state === 'signed' && sig.view?.personio === 'failed';
  return (
    <div className="flex items-center gap-3">
      {/* After a failed filing, filing it is the stage's one main action. */}
      {refileable && (
        <Button type="button" variant="primary" size="md" className="rounded" disabled={sig.busy !== null} onClick={() => sig.refile(draft.template)}>
          {sig.busy === 'personio' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FolderUp className="mr-1.5 h-4 w-4" />} File in Personio
        </Button>
      )}
      {state && SENDABLE.has(state) && (
        <>
          <Button type="button" variant={sendIsMain(draft, sig) ? 'primary' : 'outline'} size="md" className="rounded" aria-describedby={blocker ? SEND_REASON_ID : undefined}
            disabled={Boolean(blocker) || sig.busy !== null} onClick={() => setAsking('send')}>
            {sig.busy === 'send' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send for signature
          </Button>
          {blocker && <span id={SEND_REASON_ID} className="sr-only">{blocker}</span>}
        </>
      )}
      {state === 'out' && (
        <Button type="button" variant="ghost" size="md" className="rounded" disabled={sig.busy !== null} onClick={() => setAsking('cancel')}>
          {sig.busy === 'cancel' && <Loader2 className="h-4 w-4 animate-spin" />} Cancel request
        </Button>
      )}
      {state === 'signed' && (
        <Button type="button" variant={refileable ? 'outline' : 'primary'} size="md" className="rounded" disabled={sig.busy !== null}
          onClick={() => sig.downloadPdf(`${draft.fileName.replace(/\.docx$/i, '')} (signed).pdf`, draft.template)}>
          {sig.busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />} Download signed PDF
        </Button>
      )}

      {asking === 'send' && request && (
        <ConfirmDialog
          title="Send for signature?"
          body={`YouSign emails ${signer} now and asks them to sign this letter. If they do not sign, they get a reminder every 2 days, up to 3 times.`}
          confirmLabel={`Send to ${draft.signatory?.firstName || signer}`}
          onClose={() => setAsking(null)}
          onConfirm={() => {
            setAsking(null);
            acted.current = true;
            void sig.send(request, draft.template);
          }}
        />
      )}
      {asking === 'cancel' && (
        <ConfirmDialog
          title="Cancel the signature request?"
          body={`YouSign tells ${signer} that the request was withdrawn. You can send the letter again afterwards.`}
          confirmLabel="Cancel request"
          onClose={() => setAsking(null)}
          onConfirm={() => {
            setAsking(null);
            acted.current = true;
            void sig.cancel(draft.template);
          }}
        />
      )}
    </div>
  );
}

/** What the draft still needs before it can go anywhere, or null when it is ready. */
function draftNeeds(draft: ReferenceLetterDraft, { open, blocking, updating }: DraftProgress): { text: string; tone: 'red' | 'orange' | 'grey' } | null {
  if (updating) return { text: 'Updating…', tone: 'grey' };
  if (open) return { text: `${open} ${open === 1 ? 'check' : 'checks'} open.`, tone: blocking ? 'red' : 'orange' };
  if (!draft.downloadable) return { text: 'Letter not complete yet.', tone: 'grey' };
  return null;
}

export interface DraftProgress {
  open: number;
  blocking: boolean;
  updating: boolean;
}

const TONE_CLASS = { red: 'text-red', orange: 'text-orange', grey: 'text-zinc-600' } as const;

/**
 * What a signed letter's Personio filing asks of the BP, after "Signed by …". The badge already
 * says where it stands, so filed and being filed add nothing. ⚠️ Nothing for a filing not started
 * yet: right after signing it is due on the next tick, and "upload it by hand" then would invite a
 * second document in the employee's file.
 */
const FILING_LINE: Record<string, string> = {
  failed: ' Filing it in Personio did not work.',
  uncertain: ' Check Personio before you upload it by hand.',
  skipped: ' No Personio record is linked: check the right profile and upload it by hand.',
};

/**
 * The one line under the employee in the header: what the status means and what to do next. It
 * replaces the banners that used to repeat the badge (David, Oct 15 2026). Announced as it changes,
 * and it describes Download (`STATUS_LINE_ID`), so a greyed-out button is never the only clue.
 */
export function StatusLine({ draft, sig, progress }: { draft: ReferenceLetterDraft; sig: LetterSignature; progress: DraftProgress }) {
  const v = sig.view;
  const signer = v?.signerName || draft.signatory?.name || 'the supervisor';
  const since = day(v?.sentAt);
  let text: string;
  let tone: 'red' | 'orange' | 'grey' = 'grey';
  // Marked Done by hand says so first: whatever else is true, the team has closed it.
  if (v?.done) {
    return <p id={STATUS_LINE_ID} role="status" data-testid="status-line" className={`text-sm ${TONE_CLASS.grey}`}>{markedDoneText(v.done)}.</p>;
  }
  switch (v?.state) {
    case 'sending':
      text = `Being sent to ${signer}…`;
      break;
    case 'out':
      text = `Sent to ${signer}${since ? ` on ${since}` : ''}. To change it, cancel and send again.`;
      break;
    case 'uncertain':
      text = `YouSign did not confirm the send to ${signer}. Do not send it again; this updates within minutes.`;
      break;
    case 'signed': {
      const on = day(v.updatedAt);
      text = `Signed by ${signer}${on ? ` on ${on}` : ''}.${FILING_LINE[v.personio ?? ''] ?? ''}`;
      break;
    }
    default: {
      // Never sent, or sendable again: what the draft still needs, then who can sign it.
      const before = v?.state === 'declined' ? `${signer} declined to sign; the reason is in Boris's Slack message. `
        : v?.state === 'expired' ? `The request expired without ${signer}'s signature. `
          : v?.state === 'canceled' ? 'The signature request was cancelled. ' : '';
      const needs = draftNeeds(draft, progress);
      if (needs) {
        text = `${before}${needs.text}`;
        tone = needs.tone;
      } else if (draft.signatory === null) {
        text = `${before}Ready to download. Personio has no signing supervisor, so it cannot go out for signature.`;
      } else if (draft.signatory && !draft.signatory.active) {
        text = `${before}Ready to download. ${draft.signatory.name} no longer works at FINN, so it cannot go to them for signature.`;
      } else {
        text = `${before}Ready to download or send for signature.`;
      }
    }
  }
  return <p id={STATUS_LINE_ID} role="status" data-testid="status-line" className={`text-sm ${TONE_CLASS[tone]}`}>{text}</p>;
}

export const STATUS_LINE_ID = 'letter-status-line';

/** A failed signature call, beside the header (never instead of it): it says what went wrong. */
export function SignatureError({ sig }: { sig: LetterSignature }) {
  if (!sig.error) return null;
  return (
    <div id={ERROR_ID} tabIndex={-1} className="flex flex-wrap items-center gap-3 outline-none">
      <div className="min-w-0 flex-1"><Banner variant="alert" description={sig.error} /></div>
      {!sig.view && <Button type="button" variant="outline" size="sm" className="rounded" onClick={sig.reload}>Try again</Button>}
    </div>
  );
}
